import { supabaseService } from '../db/supabase';
import { syncService } from '../services/sync';
import { UIComponents } from '../ui/components';
import type { AppState, SolicitacaoLaudoRegistro } from '../types';

const byId = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

const N8N_LAUDO_WEBHOOK = 'https://n8n.arthuraaferreira.com.br/webhook/inventariarutf-laudo';

const setGlobalLoading = (active: boolean) => {
  const bar = document.getElementById('global-loading-bar');
  if (!bar) return;
  bar.classList.toggle('active', active);
};

export class ModuloLaudo {
  private fotosSelecionadas: File[] = [];
  private objectUrlPool: string[] = [];
  private onFotosChange = (event: Event) => {
    const input = event.target as HTMLInputElement | null;
    if (!input) return;
    const arquivos = Array.from(input.files || []).slice(0, 3);
    this.fotosSelecionadas = arquivos;
    this.atualizarPreviewFotos();
  };

  constructor(private state: AppState) {}

  inicializar() {
    this.resetar();
    this.vincularEventos();
  }

  private limparPreviewFotos() {
    const preview = byId('laudo-fotos-preview');
    if (preview) {
      preview.innerHTML = '';
    }
    this.objectUrlPool.forEach(url => URL.revokeObjectURL(url));
    this.objectUrlPool = [];
  }

  private atualizarPreviewFotos() {
    const preview = byId('laudo-fotos-preview');
    if (!preview) return;

    this.limparPreviewFotos();

    this.fotosSelecionadas.forEach((file, index) => {
      const figure = document.createElement('figure');
      const img = document.createElement('img');
      const caption = document.createElement('figcaption');

      const url = URL.createObjectURL(file);
      this.objectUrlPool.push(url);
      img.src = url;
      img.alt = `Foto ${index + 1}`;
      caption.textContent = `${index + 1}/${this.fotosSelecionadas.length}`;

      figure.appendChild(img);
      figure.appendChild(caption);
      preview.appendChild(figure);
    });
  }

  private formatarData(data: Date) {
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    return `${dia}/${mes}/${ano}`;
  }

  private gerarPathArquivoLaudo(index: number, originalName: string) {
    const campusId = this.state.campus?.id ?? 'desconhecido';
    const timestamp = Date.now();
    const rawExt = originalName.split('.').pop()?.toLowerCase() || 'jpg';
    const sanitizedExt = /^[a-z0-9]{1,5}$/.test(rawExt) ? rawExt : 'jpg';
    return `laudos/${campusId}/${timestamp}_${index}.${sanitizedExt}`;
  }

  private resetar() {
    this.state.laudoBemConfirmado = false;
    this.state.laudoLocalConfere = true;
    this.state.laudoBem = null;
    this.fotosSelecionadas = [];
    this.limparPreviewFotos();

    const etapa2 = byId('laudo-etapa2');
    const alerta = byId('laudo-bem-alerta');
    const localManual = byId('laudo-local-manual');
    const resultado = byId('laudo-bem-resultado');
    const inputFotos = byId<HTMLInputElement>('laudo-fotos');

    etapa2?.classList.add('hidden');
    alerta?.classList.add('hidden');
    localManual?.classList.add('hidden');

    if (resultado) {
      resultado.textContent = 'Aguardando busca.';
    }

    if (inputFotos) {
      inputFotos.value = '';
    }
  }

  async buscarBem() {
    const inputTombo = byId<HTMLInputElement>('laudo-tombo');
    const resultado = byId('laudo-bem-resultado');

    if (!inputTombo || !resultado) return;

    const tombo = inputTombo.value.trim();

    if (!tombo) {
      UIComponents.warning('Informe o número do bem.');
      return;
    }

    if (!this.state.campus) return;

    resultado.textContent = 'Consultando base patrimonial...';

    try {
      const bem = await syncService.buscarBemPorTombo(tombo);

      if (!bem) {
        this.state.laudoBem = null;
        resultado.textContent = 'Bem não localizado na base.';
        return;
      }

      const descricaoComposta = [
        (bem as any).descricao,
        (bem as any).caracteristica
      ].filter(Boolean).join(' - ');

      const campusOrigem = (bem as any).campus_origem || '-';
      const campusDivergente = campusOrigem !== '-' && campusOrigem !== this.state.campus?.id;
      const campusHtml = campusDivergente
        ? `${campusOrigem} <span class="campus-origem-alert">⚠ Bem de outro câmpus!</span>`
        : campusOrigem;

      resultado.innerHTML = `
        <strong>Bem localizado:</strong> ${descricaoComposta || '-'}<br>
        <small>Tombo: ${tombo}</small><br>
        <small>Campus: ${campusHtml}</small><br>
        <small>Setor: ${(bem as any).setor_sigla || '-'}${(bem as any).setor_descricao ? ` · ${(bem as any).setor_descricao}` : ''}</small><br>
        <small>Local físico: ${(bem as any).local_fisico || '-'}</small>
      `;

      this.state.laudoBem = bem;

    } catch (error) {
      console.error('Erro ao buscar bem:', error);
      resultado.textContent = `Erro na consulta: ${(error as Error).message}`;
    }
  }

  confirmarBem() {
    const etapa2 = byId('laudo-etapa2');
    const alerta = byId('laudo-bem-alerta');
    const localCadastro = byId('laudo-local-cadastro');

    if (!this.state.laudoBem) {
      alerta?.classList.remove('hidden');
      etapa2?.classList.add('hidden');
      return;
    }

    alerta?.classList.add('hidden');
    etapa2?.classList.remove('hidden');

    const bem = this.state.laudoBem as any;
    const setor = [bem.setor_sigla, bem.setor_descricao].filter(Boolean).join(' · ');
    const localFisico = bem.local_fisico || 'Não informado';

    if (localCadastro) {
      localCadastro.innerHTML = `Local do cadastro: <strong>${localFisico}</strong>${setor ? `<br><small>Setor: ${setor}</small>` : ''}`;
    }

    this.state.laudoBemConfirmado = true;
  }

  async solicitar() {
    if (!this.state.laudoBem) {
      alert('Confirme o bem antes de solicitar laudo.');
      return;
    }

    if (!this.state.campus) return;

    // Validar campos
    const inputTombo = byId<HTMLInputElement>('laudo-tombo');
    const inputMotivo = byId<HTMLInputElement>('laudo-motivo');
    const inputDetalhes = byId<HTMLTextAreaElement>('laudo-detalhes');
    const inputUsuario = byId<HTMLInputElement>('laudo-usuario');
    const inputUnidade = byId<HTMLInputElement>('laudo-unidade');
    const inputProcesso = byId<HTMLInputElement>('laudo-processo');
    const inputFotos = byId<HTMLInputElement>('laudo-fotos');

    if (!inputTombo || !inputMotivo || !inputDetalhes || !inputUsuario ||
        !inputUnidade || !inputProcesso || !inputFotos) {
      return;
    }

    const fotosSelecionadas = this.fotosSelecionadas;

    if (!fotosSelecionadas.length) {
      UIComponents.warning('Adicione ao menos uma foto (máximo de 3).');
      return;
    }

    const tombo = inputTombo.value.trim();
    const motivo = inputMotivo.value.trim();
    const detalhes = inputDetalhes.value.trim();
    const usuario = inputUsuario.value.trim();
    const unidade = inputUnidade.value.trim();
    const processo = inputProcesso.value.trim();

    if (!motivo) {
      UIComponents.warning('Informe o motivo da solicitação.');
      return;
    }
    if (!detalhes) {
      UIComponents.warning('Descreva os detalhes da solicitação.');
      return;
    }
    if (!usuario) {
      UIComponents.warning('Informe o usuário solicitante.');
      return;
    }
    if (!unidade) {
      UIComponents.warning('Informe a Unidade SEI.');
      return;
    }
    if (!processo) {
      UIComponents.warning('Informe o número do processo SEI.');
      return;
    }

    const manualCampus = byId<HTMLInputElement>('laudo-local-campus')?.value.trim() || '';
    const manualSede = byId<HTMLInputElement>('laudo-local-sede')?.value.trim() || '';
    const manualBloco = byId<HTMLInputElement>('laudo-local-bloco')?.value.trim() || '';
    const manualAndar = byId<HTMLInputElement>('laudo-local-andar')?.value.trim() || '';
    const manualSala = byId<HTMLInputElement>('laudo-local-sala')?.value.trim() || '';

    if (!this.state.laudoLocalConfere && !manualCampus) {
      UIComponents.warning('Informe o campo Campus no bloco manual.');
      return;
    }

    const loading = UIComponents.showLoading('Enfileirando solicitação de laudo...');
    setGlobalLoading(true);

    try {
      const urls = await Promise.all(
        fotosSelecionadas.map((file, index) =>
          supabaseService.uploadFoto(file, this.gerarPathArquivoLaudo(index + 1, file.name))
        )
      );

      const agora = new Date();
      const dataSolicitacao = this.formatarData(agora);
      const horarioSolicitacao = agora.toISOString();

      const registroBase: SolicitacaoLaudoRegistro = {
        campus_origem: this.state.campus!.id,
        tombo,
        usuario,
        unidade,
        processo,
        motivo,
        detalhes,
        fotos: urls,
        local_campus: this.state.laudoLocalConfere ? this.state.campus?.nome || null : manualCampus || null,
        local_sede: this.state.laudoLocalConfere
          ? (this.state.campus as any)?.sedes?.[String(this.state.sede)]?.nome || this.state.sede || null
          : manualSede || null,
        local_bloco: this.state.laudoLocalConfere ? this.state.bloco || null : manualBloco || null,
        local_andar: this.state.laudoLocalConfere ? this.state.andar || null : manualAndar || null,
        local_sala: this.state.laudoLocalConfere ? this.state.sala || null : manualSala || null,
        data_solicitacao: dataSolicitacao,
        hora_solicitacao: horarioSolicitacao,
        status: 'pendente',
      } as SolicitacaoLaudoRegistro;

      const registroSalvo = await supabaseService.registrarSolicitacaoLaudo(registroBase);

      try {
        await this.dispararWebhookLaudo({ ...registroSalvo, fotos: urls, hora_solicitacao: horarioSolicitacao });
        if (registroSalvo.id) {
          await supabaseService.atualizarStatusSolicitacaoLaudo(
            registroSalvo.id,
            'enfileirado',
            horarioSolicitacao
          );
        }
        UIComponents.success('Solicitação enviada para a fila do n8n.');
        this.resetar();
      } catch (webhookError) {
        console.error('Erro ao disparar webhook do laudo:', webhookError);
        if (registroSalvo.id) {
          await supabaseService.atualizarStatusSolicitacaoLaudo(registroSalvo.id, 'erro_webhook');
        }
        UIComponents.warning('Solicitação salva, porém o webhook do n8n não respondeu. Ele será reenviado automaticamente.');
      }

    } catch (error) {
      console.error('Erro ao solicitar laudo:', error);
      UIComponents.error(`Erro ao solicitar laudo: ${(error as Error).message}`);
    } finally {
      UIComponents.hideLoading(loading);
      setGlobalLoading(false);
    }
  }

  private vincularEventos() {
    const btnBuscar = byId('btn-laudo-buscar');
    const btnConfirmar = byId('btn-laudo-confirmar-bem');
    const btnSolicitar = byId('btn-solicitar-laudo');
    const btnLocalConfere = byId('btn-laudo-local-confere');
    const btnLocalDiverge = byId('btn-laudo-local-diverge');

    if (btnBuscar) btnBuscar.onclick = () => this.buscarBem();
    if (btnConfirmar) btnConfirmar.onclick = () => this.confirmarBem();
    if (btnSolicitar) btnSolicitar.onclick = () => this.solicitar();

    if (btnLocalConfere) btnLocalConfere.onclick = () => {
      this.state.laudoLocalConfere = true;
      byId('laudo-local-manual')?.classList.add('hidden');
    };

    if (btnLocalDiverge) btnLocalDiverge.onclick = () => {
      this.state.laudoLocalConfere = false;
      byId('laudo-local-manual')?.classList.remove('hidden');
    };

    const inputFotos = byId<HTMLInputElement>('laudo-fotos');
    if (inputFotos) {
      inputFotos.onchange = this.onFotosChange;
    }
  }

  private async dispararWebhookLaudo(payload: SolicitacaoLaudoRegistro) {
    const response = await fetch(N8N_LAUDO_WEBHOOK, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`Webhook retornou ${response.status}: ${text}`);
    }
  }
}
